import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Responsive,
  useContainerWidth,
  verticalCompactor,
} from 'react-grid-layout';
import { useWidgetGridContext } from './context';
import { useWidgetGrid } from './hooks';
import { WidgetCardShell, WidgetErrorState, WidgetEmptyState } from './components';
import WidgetWrapper from './components/WidgetWrapper';
import { GridContainer, GridItem } from './WidgetGrid.styled';
import { BREAKPOINTS, BREAKPOINT_COLS, DRAG_HANDLE_OFFSET_PX, GRID_MARGIN, WIDGET_GRID_COPY } from './constants';
import { getBreakpointFromWidth, getWidgetLayoutWidth } from './utils';

const WidgetGrid = ({
  dashboardUuid,
  externalWidgetConfig,
  onExternalLayoutChange,
  renderWidget,
  readOnly,
}) => {
  const { userId, isEditMode } = useWidgetGridContext();
  const { width, containerRef, mounted } = useContainerWidth();

  const isExternalMode = Boolean(externalWidgetConfig);

  const internalGrid = useWidgetGrid({
    dashboardUuid: isExternalMode ? null : dashboardUuid,
    userId,
  });

  const widgetConfig = isExternalMode ? externalWidgetConfig : internalGrid.widgetConfig;
  const configLoading = isExternalMode ? false : internalGrid.configLoading;
  const configError = isExternalMode ? null : internalGrid.configError;
  const handleLayoutChange = isExternalMode
    ? onExternalLayoutChange
    : internalGrid.handleLayoutChange;

  const rowHeight = widgetConfig?.gridConfig?.rowHeight ?? 24;
  const margin = widgetConfig?.gridConfig?.margin ?? GRID_MARGIN;
  const horizontalContainerPadding = isExternalMode ? 0 : margin[0] + DRAG_HANDLE_OFFSET_PX;

  const canInteract = isEditMode && !readOnly;

  const dragConfig = useMemo(
    () => ({
      enabled: canInteract,
      handle: '.widget-drag-handle',
    }),
    [canInteract],
  );

  const resizeConfig = useMemo(
    () => ({
      enabled: canInteract,
      handles: ['s', 'n', 'e', 'w'],
    }),
    [canInteract],
  );

  const layouts = useMemo(
    () => widgetConfig?.layouts ?? {},
    [widgetConfig?.layouts],
  );

  const breakpoint = useMemo(
    () => getBreakpointFromWidth(width, BREAKPOINTS),
    [width],
  );

  const children = useMemo(() => {
    const widgets = widgetConfig?.widgets ?? [];
    return widgets.map((w) => {
      const layoutW = getWidgetLayoutWidth(
        w.instance_uuid,
        layouts,
        breakpoint,
        w.layout?.w,
      );
      return (
        <GridItem key={w.instance_uuid}>
          {renderWidget
            ? renderWidget(w, layoutW)
            : <WidgetWrapper widgetConfig={w} layoutW={layoutW} />}
        </GridItem>
      );
    });
  }, [widgetConfig?.widgets, layouts, breakpoint, renderWidget]);

  const renderContent = () => {
    if (configLoading) {
      return (
        <WidgetCardShell>
          <WidgetEmptyState message={WIDGET_GRID_COPY.states.loadingDashboard} />
        </WidgetCardShell>
      );
    }
    if (configError) {
      return (
        <WidgetCardShell>
          <WidgetErrorState
            message={configError.message || WIDGET_GRID_COPY.errors.dashboardLoad}
            onRetry={() => window.location.reload()}
          />
        </WidgetCardShell>
      );
    }
    if (!widgetConfig?.widgets?.length) {
      return (
        <WidgetCardShell>
          <WidgetEmptyState message={WIDGET_GRID_COPY.states.noWidgets} />
        </WidgetCardShell>
      );
    }
    return (
      mounted && (
        <Responsive
          width={width}
          breakpoints={BREAKPOINTS}
          cols={BREAKPOINT_COLS}
          layouts={layouts}
          rowHeight={rowHeight}
          margin={margin}
          containerPadding={[horizontalContainerPadding, margin[1]]}
          dragConfig={dragConfig}
          resizeConfig={resizeConfig}
          compactor={verticalCompactor}
          onLayoutChange={handleLayoutChange}
          autoSize
        >
          {children}
        </Responsive>
      )
    );
  };

  return <GridContainer ref={containerRef}>{renderContent()}</GridContainer>;
};

WidgetGrid.propTypes = {
  dashboardUuid: PropTypes.string,
  externalWidgetConfig: PropTypes.shape({
    widgets: PropTypes.array,
    layouts: PropTypes.object,
    gridConfig: PropTypes.object,
  }),
  onExternalLayoutChange: PropTypes.func,
  renderWidget: PropTypes.func,
  readOnly: PropTypes.bool,
};

WidgetGrid.defaultProps = {
  dashboardUuid: null,
  externalWidgetConfig: null,
  onExternalLayoutChange: null,
  renderWidget: null,
  readOnly: false,
};

export default WidgetGrid;
